import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import { useTooltip } from "context/TooltipProvider";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import {
  addRespondantAppeal,
  updateRespondentAssetAppeal,
} from "services/assetAppealService";
import { Asset, AssetAppeal, RespondentAssetAppealStatusEnum } from "types";

import Select, { MultiValue } from "react-select";
import { useAuth } from "context/AuthContext";

interface SelectOption {
  value: string | number;
  label: string;
}

interface RespondantAssetAppealRequestProps {
  isOpen: boolean;
  onClose: () => void;
  prohabition_request_court_registration_number?: string;
  asset_recovery_request_court_registration_number?: string;
  asset_referral?: string;
  asset?: Asset[];
  onSuccess?: (response?: any) => void; // Update to accept response
  setAppeal?: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
  // Edit mode props
  isEdit?: boolean;
  appealData?: AssetAppeal | null; // Data for edit mode
  appealId?: string; // ID for update
}

export default function RespondantAssetAppealRequest({
  isOpen,
  onClose,
  prohabition_request_court_registration_number,
  asset_recovery_request_court_registration_number,
  asset_referral,
  asset,
  onSuccess,

  // Edit mode props
  isEdit = false,
  appealData = null,
  appealId,
  setAppeal,
}: RespondantAssetAppealRequestProps) {
  const [formData, setFormData] = useState({
    court_summon_respondant_appeal_file: null as File | null,
    appeal_type: "",
    admin_unit: "",
    court_name: "",
    court_type: "",
    court_level: "",
    court_hearing_date: "",
    registration_number: "",
    registration_date: "",
    court_appeal_number: "",
    opening_date: "",
    first_hearing_date: "",
    asset: [] as string[],
  });

  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  // Reset form when popup opens/closes or when appealData changes
  useEffect(() => {
    if (isOpen) {
      if (isEdit && appealData) {
        // Populate form with existing data for edit mode
        setFormData({
          court_summon_respondant_appeal_file: null, // File handling might need special consideration
          appeal_type: appealData.appeal_type || "",
          admin_unit: appealData.admin_unit || "",
          court_name: appealData.court_name || "",
          court_type: appealData.court_type || "",
          court_level: appealData.court_level || "",
          court_hearing_date: appealData.court_hearing_date || "",
          registration_number: appealData.registration_number || "",
          registration_date: appealData.registration_date || "",
          court_appeal_number: appealData.court_appeal_number || "",
          opening_date: appealData.opening_date || "",
          first_hearing_date: appealData.first_hearing_date || "",
          asset: appealData.assets
            ? appealData.assets
                .map((a) => String((a as Asset).id))
                .filter((id): id is string => typeof id === "string")
            : Array.isArray(appealData.asset)
            ? appealData.asset.map((a) =>
                typeof a === "string" ? a : String((a as Asset).id)
              )
            : [],
        });
        // Handle existing files if needed
        // setUploadedFiles(existingFiles);
      } else {
        // Reset form for create mode
        setFormData({
          court_summon_respondant_appeal_file: null,
          appeal_type: "",
          admin_unit: "",
          court_name: "",
          court_type: "",
          court_level: "",
          court_hearing_date: "",
          registration_number: "",
          registration_date: "",
          court_appeal_number: "",
          opening_date: "",
          first_hearing_date: "",
          asset: [],
        });
        setUploadedFiles([]);
      }
    }
  }, [isOpen, isEdit, appealData]);

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string | string[] | boolean } }
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.court_name) return "Court Name is required.";
    if (!formData.court_type) return "Court Type is required.";
    if (!formData.court_level) return "Court Level is required.";
    if (!formData.registration_number)
      return "Registration Number is required.";
    if (!formData.registration_date) return "Registration Date is required.";
    if (!formData.court_appeal_number)
      return "Court Appeal Number is required.";
    return "";
  };

  const handleClose = () => {
    setFormData({
      court_summon_respondant_appeal_file: null,
      appeal_type: "",
      admin_unit: "",
      court_name: "",
      court_type: "",
      court_level: "",
      court_hearing_date: "",
      registration_number: "",
      registration_date: "",
      court_appeal_number: "",
      opening_date: "",
      first_hearing_date: "",
      asset: [],
    });
    setUploadedFiles([]);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      // Prepare form data for submission
      const submitFormData = new FormData();

      // Add the main file
      if (formData.court_summon_respondant_appeal_file) {
        submitFormData.append(
          "court_summon_respondant_appeal_file",
          formData.court_summon_respondant_appeal_file
        );
      }

      // Add form fields
      submitFormData.append("appeal_type", "appeal_by_respondant");
      const adminUnit = loginData?.admin_unit;
      if (adminUnit) {
        submitFormData.append("admin_unit", adminUnit?.toString());
      }
      submitFormData.append("court_name", formData.court_name);
      submitFormData.append("court_type", formData.court_type);
      submitFormData.append("court_level", formData.court_level);
      submitFormData.append("court_hearing_date", formData.court_hearing_date);
      submitFormData.append(
        "registration_number",
        formData.registration_number
      );
      submitFormData.append("registration_date", formData.registration_date);
      submitFormData.append(
        "court_appeal_number",
        formData.court_appeal_number
      );
      submitFormData.append("opening_date", formData.opening_date);
      submitFormData.append("first_hearing_date", formData.first_hearing_date);

      // Add props passed from parent
      if (prohabition_request_court_registration_number) {
        submitFormData.append(
          "prohabition_request_court_registration_number",
          prohabition_request_court_registration_number
        );
      }
      if (asset_recovery_request_court_registration_number) {
        submitFormData.append(
          "asset_recovery_request_court_registration_number",
          asset_recovery_request_court_registration_number
        );
      }
      if (asset_referral) {
        submitFormData.append("asset_referral", asset_referral);
      }

      submitFormData.append(
        "action_status",
        RespondentAssetAppealStatusEnum?.APPEAL_REQUESTED
      );

      // Add assets
      if (formData.asset && formData.asset.length > 0) {
        formData.asset.forEach((assetId) => {
          submitFormData.append("asset", assetId);
        });
      }

      // Add additional uploaded files
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          submitFormData.append("court_summon_respondant_appeal_file", file);
        });
      }

      let response;
      if (isEdit && appealId) {
        // Update existing appeal
        response = await updateRespondentAssetAppeal(appealId, submitFormData);
        showTooltip("Respondent appeal updated successfully!", "success");
        setAppeal?.(response.respondant ?? response);
      } else {
        // Create new appeal
        response = await addRespondantAppeal(submitFormData);
        showTooltip(
          "Respondent appeal request submitted successfully!",
          "success"
        );
      }

      handleClose();

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error: any) {
      console.error("Error submitting appeal request:", error);
      const errorMessage =
        error.response?.data?.message ||
        `Failed to ${isEdit ? "update" : "submit"} appeal request`;
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isEdit
          ? "Edit Respondent Asset Appeal"
          : "Respondent Asset Appeal Request"
      }
      description={
        isEdit
          ? "Update the respondent asset appeal"
          : "Submit an appeal request for respondent assets"
      }
      w="700px"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Assets Selection */}
        <div className="flex flex-col">
          {asset && asset.length > 0 && (
            <div className="flex flex-col">
              <label className="referral-form-label">Assets</label>
              <Select<SelectOption, true>
                className="asset-form-field p-5"
                classNamePrefix="rs"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: "none",
                    padding: 0,
                    background: "transparent",
                    margin: "0",
                    color: "black",
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? "transparent"
                      : state.isFocused
                      ? "transparent"
                      : "white",
                    color: state.isSelected ? "white" : "#212121",
                    fontSize: "14px",
                  }),
                }}
                isMulti
                options={asset?.map((asset) => ({
                  value: asset.id as string,
                  label: asset.asset_unique_id,
                }))}
                value={
                  formData.asset
                    ? asset
                        .filter((asset) =>
                          formData.asset.includes(asset.id as string)
                        )
                        .map((asset) => ({
                          value: asset.id as string,
                          label: asset.asset_unique_id,
                        }))
                    : []
                }
                onChange={(selectedOptions: MultiValue<SelectOption>) => {
                  const selectedValues = selectedOptions.map(
                    (option) => option.value
                  );
                  handleInputChange({
                    target: {
                      name: "asset",
                      value: selectedValues as string[],
                    },
                  });
                }}
                placeholder="Select Assets"
              />
            </div>
          )}
        </div>

        {/* Court Information */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Court Name</label>
            <input
              type="text"
              className="referral-form-field"
              name="court_name"
              value={formData.court_name}
              onChange={handleInputChange}
              placeholder="Enter court name"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Court Type</label>
            <input
              className="referral-form-field"
              name="court_type"
              value={formData.court_type}
              onChange={handleInputChange}
              required
              placeholder="Court Type"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Court Level</label>
            <input
              className="referral-form-field"
              name="court_level"
              value={formData.court_level}
              onChange={handleInputChange}
              placeholder="Court Level"
              required
            />
          </div>
          <div className="flex flex-col">
            <label className="referral-popup-label">Registration Number</label>
            <input
              type="text"
              className="referral-form-field"
              name="registration_number"
              value={formData.registration_number}
              onChange={handleInputChange}
              placeholder="Enter registration number"
              required
            />
          </div>
        </div>

        {/* Registration Information */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Court Hearing Date</label>
            <input
              type="date"
              className="referral-form-field"
              name="court_hearing_date"
              value={formData.court_hearing_date}
              onChange={handleInputChange}
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Registration Date</label>
            <input
              type="date"
              className="referral-form-field"
              name="registration_date"
              value={formData.registration_date}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        {/* Court Appeal Information */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Court Appeal Number</label>
            <input
              type="text"
              className="referral-form-field"
              name="court_appeal_number"
              value={formData.court_appeal_number}
              onChange={handleInputChange}
              placeholder="Enter court appeal number"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Opening Date</label>
            <input
              type="date"
              className="referral-form-field"
              name="opening_date"
              value={formData.opening_date}
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* First Hearing Date */}
        <div className="flex flex-col">
          <label className="referral-popup-label">First Hearing Date</label>
          <input
            type="date"
            className="referral-form-field"
            name="first_hearing_date"
            value={formData.first_hearing_date}
            onChange={handleInputChange}
          />
        </div>

        {/* Additional Supporting Documents */}
        <MultipleFileUploadComponent
          uploadedFiles={uploadedFiles}
          onFilesChange={setUploadedFiles}
          label="Court Summon Respondent Appeal File"
          // You might want to show existing files in edit mode
          // existingFiles={appealData?.files || []}
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
          }`}
        >
          {loading
            ? `${isEdit ? "Updating" : "Submitting"} Appeal...`
            : `${isEdit ? "Update" : "Submit"} Appeal Request`}
        </button>
      </form>
    </BasePopup>
  );
}
